/** One MKDD kart length in metres (see the header for why 1.6). */
export const ARENA_KL = 1.6;
/** Metres per MKDD unit. */
const U = ARENA_KL / 200;
/** MKDD frames per second. */
const F = 60;
const DEG = Math.PI / 180;
/** u/f (MKDD units per frame) → m/s. */
const UF = U * F;
/** Kart physics (Red Fire, engine class 1 — the spec's test kart). SI units. */
export const ARENA_KART = {
    /** top speed 50 u/f = 108 km/h = 15 KL/s */
    top: 50 * UF,
    /** accel phase 1: 0 → 22.5 u/f in 37 frames, linear */
    acc1To: 22.5 * UF,
    acc1: (22.5 * UF) / (37 / F),
    /** accel phase 2: v = top − (top − v)·e^(−0.0052·frames) (τ = 3.2 s) */
    acc2K: 0.0052 * F,
    /** off the gas: ×0.990/frame above 18.5 u/f, ×0.93 below, stop under ~1 u/f */
    coastSplit: 18.5 * UF,
    coastHi: 0.99,
    coastLo: 0.93,
    stop: 1 * UF,
    /** brake (B while moving): −1.05 u/f per frame */
    brake: 1.05 * UF * F,
    /** reverse: max 18.52 u/f (37 % of top), reached in ~35 frames */
    revMax: 18.52 * UF,
    revAcc: (18.52 * UF) / (35 / F),
    /** steering, full stick: 1.41°/frame = 84.6°/s at EVERY speed. Half stick
     *  is 0.98°/frame, so rate ∝ |stick|^0.525 (fits both points exactly). */
    steer: 1.41 * F * DEG,
    steerExp: 0.525,
    /** steer ramp-in: full rate after ~6 frames */
    steerRamp: F / 6,
    /** slip angle (nose inside the travel): 18° at 40 u/f, 8–12° at 10 u/f →
     *  18°·√(v / 40 u/f), capped a hair over 1 (≈ 20° at top speed). */
    slip: 18 * DEG,
    slipV: 40 * UF,
    slipCap: 1.12,
    /** how fast the nose may swing toward its target outside a drift (the
     *  post-drift realign); normal steering slip never needs this much. */
    noseRate: 4.5 * F * DEG,
    // ---- drift (power slide) ----
    /** a drift needs ≥ 23.1 u/f (50 km/h, `CheckDriftTurbo`); it ends below it */
    driftMin: 23.1 * UF,
    /** stick must be past this to start a drift in that direction */
    driftStick: 0.3,
    /** nose yaw: a 6-frame kick at 3.1°/frame, then 1.7–2.0°/frame */
    driftKickT: 6 / F,
    driftKickRate: 3.1 * F * DEG,
    driftNoseRate: 1.85 * F * DEG,
    /** counter-steer (stick to the outside) cuts the nose yaw to 0.15–0.2°/f */
    driftCounterRate: 0.18 * F * DEG,
    /** the nose settles at 70–76° of slip against its travel */
    driftSlipMax: 76 * DEG,
    driftSlipMin: 15 * DEG,
    /** the TRAVEL bends slowly: 0.7–1.2°/frame (radius 2100–3800 u) — 0.95 at
     *  neutral stick, ±0.25 toward / against the turn */
    driftPath: 0.95 * F * DEG,
    driftPathStick: 0.25 * F * DEG,
    // ---- mini-turbo ----
    /** push the stick past 0.5 to the OUTSIDE and back within 30 frames = one push */
    mtStick: 0.5,
    mtWindow: 30 / F,
    /** spark level +1 per 2 pushes; level 2 (4 pushes) arms the boost */
    mtArmFlicks: 4,
    /** releasing the drift fires a 21-frame boost (Red Fire's mMTBoostMax)… */
    mtBoostT: 21 / F,
    /** …that peaks at 64.8 u/f ≈ 1.3× top */
    mtBoostTop: 64.8 * UF,
    /** ramp so a boost fired at a typical drift speed (~45 u/f) peaks right at
     *  the end of its 21 frames: (64.8 − 45) / 21 u/f per frame */
    mtBoostAcc: ((64.8 - 45) / 21) * UF * F,
    // ---- walls ----
    /** glancing wall: slide along it and lose 20–35 % (axis-zeroing already
     *  removes the normal part; this trims the tangential part). Head-on stops
     *  dead with no rebound — that falls out of the normal part being zeroed. */
    wallKeep: 0.85,
    /** blocked speed (m/s) above which a wall contact counts as an impact */
    wallImpact: 2,
    // ---- vertical (Neon arena maps have pads/roofs; MKDD Block City has none) ----
    /** rise / fall gravity outside a tumble — UNCHANGED from the pre-MKDD arena
     *  kart so pad apexes (and therefore which roofs are reachable) stay put */
    gravityUp: 26,
    gravityDown: 44,
    /** pad launch scale (unchanged from the pre-MKDD arena kart) */
    padMul: 0.55,
};
/** Getting hit by a blast [measured; decomp KartCrash::MakeBombCrash]. */
export const ARENA_HIT = {
    /** control is lost for 100 frames */
    stunMs: Math.round((100 / F) * 1000),
    /** then 200 frames invincible + flashing — immune ~5 s from the hit */
    immuneMs: Math.round(((100 + 200) / F) * 1000),
    /** thrown ≈ 890 u up, peak at +36 frames: v0 = 2h/t, g = 2h/t² */
    tumbleVy: (2 * 890 * U) / (36 / F),
    tumbleG: (2 * 890 * U) / ((36 / F) * (36 / F)),
};
/** Bombs [measured; decomp constants 120 / 2.75 / 0.4 / −0.05]. */
export const ARENA_BOMB = {
    /** held per driver (sMaxHoldMiniBombNum) */
    maxHeld: 5,
    /** a taken item box returns after 60 inactive + 20 grow frames */
    boxRespawnMs: Math.round((80 / F) * 1000),
    /** gravity −4 u/f² (14,400 u/s²) — a bomb arc is short and snappy */
    gravity: 4 * U * F * F,
    /** horizontal drag ×0.997 per frame */
    drag: 0.997,
    /** forward lob: 120 + 0.83 × kartSpeed horizontal, 38–40 up (≈14°) */
    throwH: 120 * UF,
    throwHK: 0.83,
    throwUp: 39 * UF,
    /** stick-up LONG throw: 291 horizontal, 63.6 up (Neon: aim pitch blends to it) */
    longH: 291 * UF,
    longUp: 63.6 * UF,
    /** leaves ~145 above the ground, just past the nose */
    launchUp: 145 * U,
    launchFwd: 1.0,
    /** drop behind (stick down): ≈20 u/f backward, −2.5 down, from the bomb
     *  stack height (+160..245 above the kart) so it lands ~9 frames later,
     *  ~340 u behind, then SITS */
    dropV: 20 * UF,
    dropVy: -2.5 * UF,
    dropUp: 180 * U,
    dropBack: 1.1,
    /** a dropped bomb blows 90 frames after it lands (and flashes all along) */
    dropFuseMs: Math.round((90 / F) * 1000),
    /** a bomb that never lands blows after 300 frames */
    airFuseMs: Math.round((300 / F) * 1000),
    /** the thrower can't touch / be blasted by its own bomb for ~0.6 s */
    ownSafeMs: 600,
    /** bomb ↔ kart touch (kart origin to bomb), horizontal / vertical */
    contactR: 120 * U,
    contactY: 160 * U,
    /** blast: a kart whose origin is within ≈1360 u (6.8 KL) is hit… */
    blastR: 1360 * U,
    /** …6 frames after the detonation (any distance tested) */
    blastDelayMs: Math.round((6 / F) * 1000),
    /** the explosion itself lasts 53 frames (visual) */
    blastShowMs: Math.round((53 / F) * 1000),
};
/** Chase camera — a rigid boom, MKDD `bombMinidata` {550, 13°, −200/−150,
 *  fovy 60/50}, sized in kart lengths on the kart you SEE. */
export const ARENA_CAM = {
    /** boom 550 u = 2.75 KL */
    boom: 550 * U,
    pitch: 13 * DEG,
    /** look point straight above the kart: 260 u (full screen) / 210 u (split) */
    lookFull: 260 * U,
    lookSplit: 210 * U,
    fovFull: 60,
    fovSplit: 50,
    /** yaw += 0.2·wrap(travel − yaw) per frame toward the direction of TRAVEL;
     *  held while the kart is (nearly) stopped */
    yawGain: 0.2,
    holdBelow: 1 * UF,
    near: 10 * U,
};
/** Signed wrap to (−π, π]. */
function wrap(a) {
    while (a > Math.PI)
        a -= 2 * Math.PI;
    while (a <= -Math.PI)
        a += 2 * Math.PI;
    return a;
}
/** Signed travel speed (+ forward, − reverse) and TRAVEL heading of a kart,
 *  derived from its velocity + nose. Heading convention matches the rest of
 *  the game: forward = (−sin h, −cos h). Writes into `out` (no allocation). */
export function arenaKartTravel(k, out) {
    const hsp = Math.sqrt(k.vx * k.vx + k.vz * k.vz);
    if (hsp < 1e-4) {
        out.speed = 0;
        out.head = k.yaw;
        return;
    }
    const along = k.vx * -Math.sin(k.yaw) + k.vz * -Math.cos(k.yaw);
    const dir = along >= 0 ? 1 : -1;
    out.speed = hsp * dir;
    out.head = Math.atan2(-k.vx * dir, -k.vz * dir);
}
const trv = { speed: 0, head: 0 };
/** ARENA kart drive: the MKDD speed curves, steering, slip, drift, mini-turbo,
 *  hop, pads and gravity for ONE step. Sets k.vx/vy/vz, k.yaw (the NOSE),
 *  k.roll/pitch and the arena drift fields; stepVehicle then integrates with
 *  its shared collision and calls `arenaKartAfterMove`. */
export function stepArenaKartDrive(cfg, k, c, world, dt) {
    const K = ARENA_KART;
    arenaKartTravel(k, trv);
    let speed = trv.speed;
    let head = trv.head;
    const grounded = Math.abs(k.vy) < 0.01;
    // ---- steering input, ramped in over ~6 frames
    const steerIn = c.steer;
    let sa = k.steerAmt ?? 0;
    const ramp = K.steerRamp * dt;
    sa += Math.max(-ramp, Math.min(ramp, steerIn - sa));
    k.steerAmt = sa;
    // ---- drift state machine
    let dd = k.driftDir ?? 0;
    let dT = k.driftT ?? 0;
    let flicks = k.mtFlicks ?? 0;
    let outT = k.mtOutT ?? 0;
    let boostT = k.boostT ?? 0;
    if (dd !== 0) {
        if (!c.drift || speed < K.driftMin || c.tumble) {
            // released with the boost armed → mini-turbo (a slow-down or a hit just ends it)
            if (!c.drift && !c.tumble && speed >= K.driftMin && flicks >= K.mtArmFlicks)
                boostT = K.mtBoostT;
            dd = 0;
            dT = 0;
            flicks = 0;
            outT = 0;
        }
        else {
            dT += dt;
            // mini-turbo pushes: stick past 0.5 to the OUTSIDE and back inside 30 frames
            const outside = steerIn * dd < -K.mtStick;
            if (outside)
                outT = outT > 0 ? outT + dt : dt;
            else if (outT > 0) {
                if (outT <= K.mtWindow && flicks < K.mtArmFlicks)
                    flicks++;
                outT = 0;
            }
        }
    }
    else if (c.drift && !c.tumble && grounded && Math.abs(steerIn) > K.driftStick && speed >= K.driftMin) {
        dd = steerIn > 0 ? 1 : -1;
        dT = 0;
        flicks = 0;
        outT = 0;
    }
    // ---- speed: two-phase accel, coast, brake → reverse, mini-turbo
    const gas = c.throttle > 0.15;
    const brake = c.throttle < -0.15;
    if (c.tumble) {
        speed = 0; // a bomb hit zeroes the velocity; control is gone
    }
    else if (boostT > 0) {
        if (speed < K.mtBoostTop)
            speed = Math.min(K.mtBoostTop, Math.max(speed, 0) + K.mtBoostAcc * dt);
        boostT = Math.max(0, boostT - dt);
    }
    else if (gas) {
        if (speed < 0)
            speed = Math.min(0, speed + K.brake * dt);
        else if (speed < K.acc1To)
            speed = Math.min(K.acc1To, speed + K.acc1 * dt);
        else if (speed <= K.top)
            speed = K.top - (K.top - speed) * Math.exp(-K.acc2K * dt);
        else
            speed = Math.max(K.top, speed * Math.pow(K.coastHi, dt * F)); // over the top (boost): settle back
    }
    else if (brake) {
        if (speed > 0)
            speed = Math.max(0, speed - K.brake * dt);
        else
            speed = Math.max(-K.revMax, speed - K.revAcc * dt);
    }
    else if (speed > 0) {
        speed *= Math.pow(speed > K.coastSplit ? K.coastHi : K.coastLo, dt * F);
        if (speed < K.stop)
            speed = 0;
    }
    else if (speed < 0) {
        speed *= Math.pow(K.coastLo, dt * F);
        if (speed > -K.stop)
            speed = 0;
    }
    // ---- heading (TRAVEL) and nose. Steer right (+) turns right = yaw DOWN.
    const dir = speed < 0 ? -1 : 1;
    let yaw = k.yaw;
    if (dd !== 0) {
        // power slide: the travel bends slowly, the nose swings far inside
        const sIn = Math.max(-1, Math.min(1, steerIn * dd));
        head -= dd * (K.driftPath + K.driftPathStick * sIn) * dt;
        const noseRate = dT < K.driftKickT ? K.driftKickRate : sIn < -K.mtStick ? K.driftCounterRate : K.driftNoseRate;
        yaw -= dd * noseRate * dt;
        const slipIn = dd * wrap(head - yaw);
        if (slipIn > K.driftSlipMax)
            yaw = head - dd * K.driftSlipMax;
        else if (slipIn < K.driftSlipMin && dT >= K.driftKickT)
            yaw = head - dd * K.driftSlipMin;
    }
    else {
        if (Math.abs(speed) > 0.05) {
            const mag = Math.pow(Math.abs(sa), K.steerExp) * (sa < 0 ? -1 : 1);
            head -= mag * K.steer * dt * dir;
        }
        const slipF = Math.min(K.slipCap, Math.sqrt(Math.abs(speed) / K.slipV));
        const target = head - dir * sa * K.slip * slipF;
        const d = wrap(target - yaw);
        const maxD = K.noseRate * dt;
        yaw += Math.max(-maxD, Math.min(maxD, d));
    }
    k.yaw = wrap(yaw);
    k.vx = -Math.sin(head) * speed;
    k.vz = -Math.cos(head) * speed;
    k.driftDir = dd;
    k.driftT = dT;
    k.mtFlicks = flicks;
    k.mtOutT = outT;
    k.boostT = boostT;
    k.boostMul = 0;
    k.driftCharge = 0;
    // ---- vertical: the R hop is (almost) cosmetic in MKDD; pads + roofs are Neon's
    if (c.ascend && !c.tumble && (cfg.jumpVel ?? 0) > 0 && grounded)
        k.vy = cfg.jumpVel;
    if (Math.abs(k.vy) < 2 && world.pads.length > 0) {
        for (const pad of world.pads) {
            if (pad.fling === 0)
                continue; // vertical lift shafts are for players
            const pdx = k.x - pad.x, pdz = k.z - pad.z;
            if (pdx * pdx + pdz * pdz < 2.2 * 2.2 && k.y >= pad.y - 0.6 && k.y <= pad.y + 1.8) {
                k.vy = pad.power * K.padMul;
                break;
            }
        }
    }
    k.vy -= (c.tumble ? ARENA_HIT.tumbleG : k.vy < 0 ? K.gravityDown : K.gravityUp) * dt;
    // a small body lean only — MKDD's camera never rolls and the kart barely does
    const lean = dd !== 0 ? dd * 0.1 : sa * 0.06 * Math.min(1, Math.abs(speed) / K.top);
    k.roll = -lean;
    k.pitch = 0;
}
/** After the collision integration. `wallDv` is the biggest axis speed a
 *  wall just removed (−1 = no contact). ANY wall touch ends a drift; an
 *  IMPACT (more than ~2 m/s into the wall) also trims the slide speed —
 *  glancing: lose 20–35 %; head-on already stopped dead. Scraping along a wall
 *  while steering into it (a fraction of a m/s per frame) costs nothing extra,
 *  or the trim would compound every frame and glue the kart to the wall. */
export function arenaKartAfterMove(k, wallDv) {
    if (wallDv < 0)
        return;
    if (wallDv > ARENA_KART.wallImpact) {
        k.vx *= ARENA_KART.wallKeep;
        k.vz *= ARENA_KART.wallKeep;
    }
    if ((k.driftDir ?? 0) !== 0) {
        k.driftDir = 0;
        k.driftT = 0;
        k.mtFlicks = 0;
        k.mtOutT = 0;
    }
}
/** Mini-turbo spark level of a drifting kart: 0 none, 1 (2 pushes), 2 armed. */
export function arenaKartSparkLevel(k) {
    if (!k.driftDir)
        return 0;
    return Math.min(2, Math.floor((k.mtFlicks ?? 0) / 2));
}
/** Forward-throw launch velocity for a kart: neutral = MKDD's forward lob,
 *  `aim` 0..1 blends to the stick-up LONG throw. Direction is the kart NOSE.
 *  Shared by the server throw, the bot solver and the benches. */
export function arenaBombThrowVel(yaw, kartSpeed, aim, out) {
    const B = ARENA_BOMB;
    const a = Math.max(0, Math.min(1, aim));
    const h = (B.throwH + B.throwHK * Math.max(0, kartSpeed)) * (1 - a) + B.longH * a;
    out.vx = -Math.sin(yaw) * h;
    out.vz = -Math.cos(yaw) * h;
    out.vy = B.throwUp * (1 - a) + B.longUp * a;
}
/** Map the replicated camera/aim pitch to the throw blend (0 = forward lob,
 *  1 = long throw). The arena camera rests the pitch at 0.16 and clamps it to
 *  0.42, so holding aim UP is the long throw — same control as before. */
export function arenaThrowAim(pitch) {
    return Math.max(0, Math.min(1, (pitch - 0.2) / (0.42 - 0.2)));
}
/** One flight step of an arena bomb: horizontal drag (×0.997/frame) and
 *  −4 u/f² gravity. The server substeps bombs through THIS function, and the
 *  bot solver / benches predict with it, so the two cannot drift apart. */
export function arenaBombFly(b, dt) {
    const B = ARENA_BOMB;
    b.x += b.vx * dt;
    b.z += b.vz * dt;
    const drag = Math.pow(B.drag, dt * F);
    b.vx *= drag;
    b.vz *= drag;
    b.vy -= B.gravity * dt;
    b.y += b.vy * dt;
}
/** Bomb flight substep (s): the server tick is ~33 ms and a lob moves ~2.4 m
 *  per tick, so it is split into ≤ 1/120 s steps (no tunnelling through walls
 *  or past a kart, landing spot within ~0.6 m). */
export const ARENA_BOMB_SUBSTEP = 1 / 120;
const flyScratch = { x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0 };
/** Where a bomb launched with this state comes down to `groundY` (flat-ground
 *  prediction with the server's own integrator). Writes the landing point and
 *  time; returns false if it is still airborne when the 5 s air fuse runs out. */
export function arenaBombLanding(x, y, z, vx, vy, vz, groundY, out) {
    const b = flyScratch;
    b.x = x;
    b.y = y;
    b.z = z;
    b.vx = vx;
    b.vy = vy;
    b.vz = vz;
    const dt = ARENA_BOMB_SUBSTEP;
    let t = 0;
    const maxT = ARENA_BOMB.airFuseMs / 1000;
    while (t < maxT) {
        arenaBombFly(b, dt);
        t += dt;
        if (b.y <= groundY) {
            out.x = b.x;
            out.z = b.z;
            out.t = t;
            return true;
        }
    }
    out.x = b.x;
    out.z = b.z;
    out.t = t;
    return false;
}
//# sourceMappingURL=arenaKart.js.map